import { useMemo, useRef, useState } from 'react';

import { Col, ConfirmationModal, Row } from 'components-antd';
import { useDispatch, useSelector } from 'react-redux';
import { getDynamicFormSelector } from 'store/selectors/requestFormProcess';
import classNames from 'classnames';
import { dynamicManager } from '../DynamicManager';
import { Options, SmartFormIcons } from 'components';
import { FORM_PROCESS_SCREEN } from 'app-constants';
import { Icons, ICON_VARIANT_TYPE } from 'pages/FormProcess/Icons';

import styles from './styles.module.scss';
import { DocumentsPopover } from './../DynamicHeader/components/DocumentsPopover';
import { useLocation } from 'react-router-dom';
import { Close, Dots } from 'components/Icons';
import { SignatureContainer } from '../Containers/DynamicViewer/components';
import { DeclineFormModal } from '../DynamicFooter/DeclineButton/DeclineFormModal';
import { updateSignatoriesEffect } from 'store/effects/formProcess';
import { createReplacePayload } from 'pages/FormProcess/components/DynamicPostForm/helper';
import { getEmailToSignerMap } from '../DynamicHeader/helper';
import { useDownloadPDF } from 'pages/Workshop/Forms/components/FormDetailModal/components/DownloadFilesModal/hooks/useDownloadPDF';

export const DynamicMobileHeader = ({ isSendDocument, pdfDocRefs, documentIds }) => {
  const {
    meta: { documentName, link } = {},
    isCanceled,
    isCompleted,
    isLoading,
    allowRegress,
    screen,
    dynamicFormDocuments,
    postFormUpdates,
  } = useSelector(getDynamicFormSelector);
  const isSignScreen = screen === FORM_PROCESS_SCREEN.Sign;
  const showDecline = !allowRegress && isSignScreen;

  const { state: formState } = useLocation<{ bundleName: string; isWebView?: boolean }>();
  const state = useMemo(() => formState, []);

  const windowState = window.history.state;
  const isWebView = state?.isWebView || windowState?.isWebView;

  const wrapper = useRef<HTMLDivElement>(null);
  const [fullWidthHeader, setFullWidthHeader] = useState(false);
  const [disabled, setDisabled] = useState(false);
  const [openDecline, setDecline] = useState(false);

  const isDeclined = screen === FORM_PROCESS_SCREEN.DeclineSign;
  const isCompleteOrCanceledOrDeclined = isCanceled || isCompleted || isDeclined;

  const [confirmation, setConfirmation] = useState<
    | {
        text: string;
        handleOk: () => void;
      }
    | undefined
  >(undefined);

  const [activeScrolledDocumentId, setScrolledDocumentId] = useState(
    dynamicFormDocuments?.[0].Id || -1,
  );

  const { downloading, downloadCombinedFile } = useDownloadPDF({
    anonymousUserDetails: dynamicManager.getAnonymousDetails(),
    isCompleted: false,
    downloadedFileName: dynamicManager.getFormName(),
    formProcessPublicId: dynamicManager.getFormProcessPublicId(),
  });

  const dispatch = useDispatch();

  const manageSendDocumentClose = () => {
    const signatories = postFormUpdates?.signatories || [];
    const isSequential = postFormUpdates?.isSequential || false;
    const replacedSignatories = signatories?.filter((item) => item.isReplaced) || [];

    if (!replacedSignatories.length) {
      dynamicManager.updatePostFormSignatureSequence({
        isSequential,
        signatories,
        ...documentIds,
      });
    } else {
      dispatch(
        updateSignatoriesEffect(
          {
            formProcessPublicId: dynamicManager.getFormProcessPublicId(),
            Signatories: createReplacePayload(replacedSignatories, isSequential),
          },
          (err, response) => {
            if (!err) {
              const { signatoriesAdded } = response.data.value;

              const emailToSignerMap = getEmailToSignerMap(signatoriesAdded);

              for (const signer of signatories) {
                const matched = emailToSignerMap[signer.Email];
                if (matched) {
                  signer.signatoryId = matched.SignatureRequestId;
                }
              }

              dynamicManager.updatePostFormSignatureSequence({
                isSequential,
                signatories,
                ...documentIds,
              });
            }
          },
        ),
      );
    }
  };

  const onCloseForm = () => {
    const { sequenceModified, hasModifiedInfo } = postFormUpdates || {
      sequenceModified: false,
      hasModifiedInfo: false,
    };

    if (isSendDocument && (sequenceModified || hasModifiedInfo)) {
      manageSendDocumentClose();
    }
    dynamicManager.handleFormExit();
  };

  const OPTIONS = [
    ...(isSignScreen && allowRegress
      ? [
          {
            label: 'Edit',
            icon: <SmartFormIcons variant="pencil" />,
            onClick: dynamicManager.gotBackToPreviousScreen,
          },
        ]
      : []),
    {
      label: 'Finish Later',
      icon: <Icons variant={ICON_VARIANT_TYPE.FINISH_SIGNING} />,
      onClick: () => onCloseForm(),
    },
    ...(showDecline
      ? [
          {
            label: 'Decline to Sign',
            icon: <Close />,
            onClick: () => setDecline(true),
          },
        ]
      : []),
    {
      label: 'Print & Sign',
      icon: <Icons variant={ICON_VARIANT_TYPE.Signature} />,
      loading: downloading,
      disableCloseOnClick: true,
      onClick: () =>
        downloadCombinedFile(
          dynamicManager.getFormProcessPublicId(),
          dynamicManager.getDynamicDocuments(),
        ),
    },
  ];

  const renderRightHeader = () => {
    if (isCanceled || isDeclined) {
      return (
        <span className={styles.closeIcon}>
          <Close color={'#fff'} onClick={() => dynamicManager.handleFormExit()} />
        </span>
      );
    } else if (
      (allowRegress && screen === FORM_PROCESS_SCREEN.Sign) ||
      screen === FORM_PROCESS_SCREEN.SendDocument
    ) {
      return (
        <div className={styles.headerContainer}>
          {isSendDocument ? (
            <div
              className={classNames(styles.editForm, styles.navigationButton)}
              onClick={dynamicManager.gotBackToPreviousScreen}
            >
              <SmartFormIcons variant="back" />
              Back
            </div>
          ) : (
            <SignatureContainer isShowMobileSigningHeader />
          )}
          <Options
            title={<Dots className={styles.optionsIcon} />}
            placement="bottom"
            options={OPTIONS}
            disabled={disabled}
            popoverClassName={styles.signingOptionsPopover}
            titleClassName={styles.optionsTitle}
            getPopupContainer={(triggerNode) => triggerNode}
          />
        </div>
      );
    } else {
      return (
        <div className={styles.headerContainer}>
          <SignatureContainer isShowMobileSigningHeader />
          <Options
            title={<Dots className={styles.optionsIcon} />}
            placement="bottom"
            options={OPTIONS}
            disabled={disabled}
            popoverClassName={styles.signingOptionsPopover}
            titleClassName={styles.optionsTitle}
            getPopupContainer={(triggerNode) => triggerNode}
          />
        </div>
      );
    }
  };

  const renderCenterHeader = () => {
    if (isCompleteOrCanceledOrDeclined) {
      return (
        <div className={styles.previewTitle}>
          <span>
            {isCompleted
              ? 'This document is complete and signed by all parties.'
              : isDeclined
              ? 'This document is declined.'
              : 'This document is voided.'}
          </span>
        </div>
      );
    } else {
      if (isSendDocument) return <></>;
      return (
        <Col className={styles.documentPopoverColumn}>
          <DocumentsPopover
            allDocuments={dynamicFormDocuments}
            scrolledDocumentId={activeScrolledDocumentId}
            isSigningMobileView
          />
        </Col>
      );
    }
  };

  return (
    <>
      {confirmation && (
        <ConfirmationModal
          confirmText={confirmation.text}
          onOk={confirmation.handleOk}
          onCancel={() => setConfirmation(undefined)}
          open={!!confirmation}
          confirmLoading={isLoading}
        />
      )}

      <div ref={wrapper} className={classNames(styles.headerRow)}>
        <Row
          className={classNames(styles.headerContentRow, {
            [styles.showBorder]: screen !== FORM_PROCESS_SCREEN.SendDocument,
            [styles.webViewHeaderContentRow]: isWebView,
            [styles.fullWidthHeader]: fullWidthHeader,
          })}
        >
          {renderCenterHeader()}
          <Col>{renderRightHeader()}</Col>
        </Row>
      </div>
      <DeclineFormModal open={openDecline} onClose={() => setDecline(false)} />
    </>
  );
};
